/**
 * DrawerAnimationWrapperComponent
 *
 * Purpose:
 * This wrapper component isolates the enter/leave animations for the drawer panel from the main DrawerComponent structure.
 *
 * Problem:
 * Angular's ViewChild system requires that any queried template elements (like the dynamic content host) exist at render time.
 * However, Angular animations such as `:leave` transitions require that elements be actually destroyed from the DOM
 * to trigger the exit animation and complete the modal lifecycle cleanly (e.g., removing the overlay, deactivating the dialog trap).
 *
 * Direct conflict:
 * - If the drawer container was conditionally removed to animate the drawer closing, ViewChild resolution for the content host would break.
 * - If the drawer container was permanently present, the leave animation would not trigger and the dialog overlay would not be dismissed.
 *
 * Solution:
 * - Create a dedicated wrapper (this component) that handles only the animation logic.
 * - Always render the DrawerComponent and its ViewChildren consistently.
 * - Animate only the wrapper's internal DOM, allowing structural removal without destabilizing DrawerComponent internals.
 * - When closing, `startAnimation()` triggers the leave transition.
 * - When animation completes, `animationDone` emits, allowing DrawerComponent to finalize dialog closure.
 */

import { Component, signal, output } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'da-drawer-animation-wrapper',
  standalone: true,
  template: `
    @if (!isClosing()) {
      <div
        @drawerAnimation
        (@drawerAnimation.done)="drawerAnimationDone()"
        class="xs:tw-w-full tw-reset tw-fixed tw-right-0 tw-top-0 tw-flex tw-h-screen tw-w-full tw-flex-col tw-bg-white tw-shadow-lg sm:tw-w-[468px] md:tw-w-[376px] lg:tw-w-[424px] xl:tw-w-[424px] 2xl:tw-w-[512px]"
        aria-labelledby="edit-role-header"
        role="dialog"
      >
        <ng-content></ng-content>
      </div>
    }
  `,
  animations: [
    trigger('drawerAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate(
          '400ms cubic-bezier(0.22, 1, 0.36, 1)',
          style({ transform: 'translateX(0)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '400ms cubic-bezier(0.55, 0, 0.55, 0.2)',
          style({ transform: 'translateX(100%)' })
        ),
      ]),
    ]),
  ],
})
export class DrawerAnimationWrapperComponent {
  public animationDone = output<void>();

  public isClosing = signal(false);

  public drawerAnimationDone(): void {
    if (this.isClosing()) {
      this.animationDone.emit();
    }
  }

  public startAnimation(): void {
    this.isClosing.set(true);
  }
}
